import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiZap, FiChevronDown, FiChevronRight, FiCheckCircle,
  FiAlertTriangle, FiDollarSign, FiCpu, FiLayers,
  FiTarget, FiTrendingUp, FiUsers, FiShield, FiCode
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';
import { AI_PARADIGM_TYPES, getRecommendedParadigm } from '../../../data/aiParadigmTypes';

const AIParadigmView = () => {
  const { state, dispatch } = useSolution();
  const [expandedParadigms, setExpandedParadigms] = useState(new Set());
  const [selectedParadigm, setSelectedParadigm] = useState(null);
  
  const requirements = state.autoArchitectRequirements || {};
  const recommended = getRecommendedParadigm(requirements);
  
  const toggleExpand = (id) => {
    setExpandedParadigms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };
  
  const handleSelectParadigm = (paradigm) => {
    setSelectedParadigm(paradigm);
    
    // Add paradigm's essential components to solution
    if (paradigm.essentialComponents) {
      dispatch({ 
        type: 'SHOW_TOAST', 
        payload: { 
          type: 'info', 
          message: `Selected ${paradigm.name} - Essential components will be added` 
        } 
      });
    }
  };
  
  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <FiZap className="w-8 h-8 text-purple-600" />
            AI Paradigm Types
          </h2>
          <p className="text-gray-600">
            Layer 6.7 — The paradigms of intelligence defining AI behavior
          </p>
        </div>
        
        {/* Recommendations */}
        {recommended.length > 0 && recommended[0].score > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl border border-purple-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-purple-800 mb-3 flex items-center gap-2">
              <FiTarget className="w-5 h-5" />
              Recommended Paradigms for Your Solution
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {recommended.map(({ paradigm, score }, i) => (
                <div 
                  key={paradigm.id}
                  className="bg-white rounded-xl p-4 border-2 border-purple-200 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => handleSelectParadigm(paradigm)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">{paradigm.icon}</span>
                      <span className="font-bold text-gray-900">{paradigm.name}</span>
                    </div>
                    {i === 0 && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold">
                        BEST
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mb-2">{paradigm.purpose}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
                        style={{ width: `${score}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-purple-600">{score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* All Paradigms */}
        <div className="space-y-4">
          {AI_PARADIGM_TYPES.paradigms.map(paradigm => {
            const isExpanded = expandedParadigms.has(paradigm.id);
            const isSelected = selectedParadigm?.id === paradigm.id;
            
            return (
              <motion.div
                key={paradigm.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden transition-all ${
                  isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'
                }`}
              >
                <button
                  onClick={() => toggleExpand(paradigm.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-4xl">{paradigm.icon}</span>
                    <div className="text-left">
                      <div className="font-bold text-gray-900 text-lg">{paradigm.name}</div>
                      <div className="text-sm text-gray-500">{paradigm.purpose}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {paradigm.category}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          paradigm.maturityLevel?.includes('Production') 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {paradigm.maturityLevel}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectParadigm(paradigm);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                        isSelected 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                      }`}
                    >
                      {isSelected ? 'Selected' : 'Select'}
                    </button>
                    {isExpanded ? <FiChevronDown className="w-5 h-5" /> : <FiChevronRight className="w-5 h-5" />}
                  </div>
                </button>
                
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-5 border-t border-gray-100 bg-gray-50 space-y-5">
                        {/* Description */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-700 mb-1">Description</h4>
                          <p className="text-sm text-gray-600">{paradigm.description}</p>
                        </div>
                        
                        {/* Function */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-700 mb-1">Function</h4>
                          <p className="text-sm text-gray-600">{paradigm.function}</p>
                        </div>
                        
                        {/* Sub Types */}
                        {paradigm.subTypes && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2">Sub-Types</h4>
                            <div className="flex flex-wrap gap-2">
                              {paradigm.subTypes.map((type, i) => (
                                <span key={i} className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Essential Components */}
                        {paradigm.essentialComponents && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                              <FiCpu className="w-4 h-4" />
                              Essential Components
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {paradigm.essentialComponents.map((comp, i) => (
                                <span key={i} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                  {comp}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Recommended Components */}
                        {paradigm.recommendedComponents && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                              <FiCheckCircle className="w-4 h-4" />
                              Recommended Components
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {paradigm.recommendedComponents.map((comp, i) => (
                                <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                  {comp}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Infrastructure */}
                        {paradigm.infrastructure && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2">Infrastructure Requirements</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {Object.entries(paradigm.infrastructure).map(([key, value]) => (
                                <div key={key} className="p-2 bg-white rounded-lg">
                                  <div className="text-xs text-gray-500 capitalize">{key}</div>
                                  <div className="text-xs font-semibold text-gray-800">{value}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Cost Profile */}
                        {paradigm.costProfile && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                              <FiDollarSign className="w-4 h-4" />
                              Cost Profile
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(paradigm.costProfile).map(([key, value]) => (
                                <div key={key} className="p-2 bg-white rounded-lg">
                                  <div className="text-xs text-gray-500 capitalize">{key}</div>
                                  <div className="text-xs font-semibold text-green-600">
                                    ${value.low} - ${value.high} {value.unit}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Use Cases */}
                        {paradigm.useCases && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                              <FiTarget className="w-4 h-4" />
                              Use Cases
                            </h4>
                            <div className="grid grid-cols-2 gap-1.5">
                              {paradigm.useCases.map((uc, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                                  <FiCheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                  {uc}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Agentic Patterns */}
                        {paradigm.agenticPatterns && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2">Agentic Patterns</h4>
                            <div className="space-y-2">
                              {paradigm.agenticPatterns.map((pattern, i) => (
                                <div key={i} className="p-2 bg-white rounded-lg">
                                  <div className="font-semibold text-gray-800 text-xs">{pattern.name}</div>
                                  <div className="text-xs text-gray-500">{pattern.description}</div>
                                  <div className="text-xs text-purple-600 mt-1">Best for: {pattern.bestFor}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Autonomy Levels */}
                        {paradigm.autonomyLevels && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2">Autonomy Levels</h4>
                            <div className="space-y-1">
                              {paradigm.autonomyLevels.map((level, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  <span className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">
                                    {level.level}
                                  </span>
                                  <span className="font-semibold text-gray-800">{level.name}</span>
                                  <span className="text-gray-500">- {level.description}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Risks */}
                        {paradigm.risks && (
                          <div className="p-3 bg-red-50 rounded-lg">
                            <h4 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
                              <FiAlertTriangle className="w-4 h-4" />
                              Risks
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {paradigm.risks.map((risk, i) => (
                                <span key={i} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                                  {risk}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Mitigations */}
                        {paradigm.mitigations && (
                          <div className="p-3 bg-green-50 rounded-lg">
                            <h4 className="text-sm font-bold text-green-800 mb-2 flex items-center gap-2">
                              <FiShield className="w-4 h-4" />
                              Mitigations
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {paradigm.mitigations.map((m, i) => (
                                <span key={i} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                  {m}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Alternatives */}
                        {paradigm.alternatives && (
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-2">When to Choose Alternatives</h4>
                            <div className="space-y-1">
                              {paradigm.alternatives.map((alt, i) => (
                                <div key={i} className="text-xs text-gray-600">
                                  <span className="font-semibold">{alt.name}</span> — {alt.when}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AIParadigmView;